import { useState, useEffect, FC } from 'react';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { parseAemStitched } from './aemParsers';
import { getAemLocale } from './localeMaps';
import { DEFAULT_ISO_LOCALE } from "../constants/locales";
import fetchContent from './fetchContent';
import { LocaleWrapperProps } from '../types/translation';
import EnvVars from '../constants/env';
import { Environment } from '../types/app';
import { useLdFlags } from '@profile-ui-mono/utilities/hooks';
import { FLAG_NAMES } from '@profile-ui-mono/utilities/constants';
import { buildContentfulUrl } from '@profile-ui-mono/utilities/helpers';
import contentfulFallback from './contentfulFallback';
import { sendLog } from '../helpers/logs/ProfileLogger';

/**
 * Set up global i18n instance using root lng
 * Using the key-values parsed from AEM
 */
const setupI18n = (translation: object): void => {
  const resources = { root: { translation } };

  i18n
    .use(initReactI18next) // passes i18n down to react-i18next
    .init({
      resources,
      lng: 'root',
      keySeparator: false, // we do not use keys in form messages.welcome
      interpolation: {
        escapeValue: false,
      },
    });
};

const I18nConfigs = {
  lng: DEFAULT_ISO_LOCALE,
  fallbackLng: DEFAULT_ISO_LOCALE,
  defaultNS: '',
  debug: false,
  resources: {},
  interpolation: { escapeValue: false },
  transSupportBasicHtmlNodes: true,
};

const useLocaleContent = (locale: string, env: Environment): boolean => {
  const [loading, setLoading] = useState<boolean>(true);
  const flags = useLdFlags();
  const contentfulEnabled = flags[FLAG_NAMES.MFE_CONTENTFUL_ENABLED];
  const isRuLocaleDisabled = flags[FLAG_NAMES.RU_LOCALE_DISABLED];

  useEffect(() => {
    if (!locale) return;

    const i18nInstance = i18n.createInstance();
    i18nInstance.use(initReactI18next).init(I18nConfigs);

    const isoLocale = getAemLocale(locale, isRuLocaleDisabled);

    (async function setupContent() {
      let content: object;

      if (contentfulEnabled) {
        const {
          CONTENTFUL_SPACE_ID,
          CONTENTFUL_ENVIRONMENT,
          CONTENTFUL_ENTRY_ID,
          CONTENTFUL_DELIVERY_TOKEN,
        } = EnvVars[env];

        const contentfulUrl = buildContentfulUrl({
          spaceId: CONTENTFUL_SPACE_ID,
          environment: CONTENTFUL_ENVIRONMENT,
          entryId: CONTENTFUL_ENTRY_ID,
          locale: isoLocale,
          token: CONTENTFUL_DELIVERY_TOKEN,
        });

        try {
          const response = await fetch(contentfulUrl);

          if (response.ok) {
            const data = await response.json();
            content = data.fields.dictionaryJson;
          } else {
            content = contentfulFallback;
          }
        } catch (e) {
          sendLog(e as Error, `Error while fetching content for locale: ${isoLocale}`, 'LocaleWrapper');
          content = contentfulFallback;
        }
      } else {
        const url = EnvVars[env].ADSK_AEM_URL;
        const rawContent = await fetchContent(url, isoLocale);
        content = parseAemStitched(rawContent);
      }

      setupI18n(content);
      setLoading(false);
    })();
  }, [locale, contentfulEnabled, isRuLocaleDisabled]);

  return loading;
};

// A render-blocking wrapper that fetches, parses, and sets up localized content using i18n
const LocaleWrapper: FC<LocaleWrapperProps> = ({ children, locale, env, skeletons = null }) => {
  const loading = useLocaleContent(locale, env);
  return loading ? skeletons : children;
};

export default LocaleWrapper;
